import { buildMockDashboard } from './mockDashboard'
import { ACCESS, PRIVACY } from '../config/ondsConfig'

const API_BASE = import.meta.env.VITE_API_BASE
const COMMITTEE_REQUEST_TIMEOUT_MS = 30000
const COMMITTEE_POLL_INTERVAL_MS = 6000
const COMMITTEE_MAX_WAIT_MS = 18 * 60 * 1000

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const fetchLiveQuote = async (symbol) => {
  const res = await fetch(`${API_BASE}/onds/quote?symbol=${encodeURIComponent(symbol)}`)
  if (!res.ok) throw new Error(`quote request failed: ${res.status}`)
  return res.json()
}

export const fetchMarketContext = async (symbol = 'ONDS') => {
  try {
    const res = await fetch(`${API_BASE}/onds/market?symbol=${encodeURIComponent(symbol)}`)
    if (!res.ok) return null
    const context = await res.json()
    return context?.groups?.length ? context : null
  } catch {
    return null
  }
}

export const fetchOptionsMarket = async (symbol = 'ONDS') => {
  try {
    const res = await fetch(`${API_BASE}/onds/options?symbol=${encodeURIComponent(symbol)}`)
    if (!res.ok) return null
    const market = await res.json()
    return market?.expiries?.length ? market : null
  } catch {
    return null
  }
}

export const fetchOfficialNews = async (symbol = 'ONDS') => {
  try {
    const res = await fetch(`${API_BASE}/onds/news?symbol=${encodeURIComponent(symbol)}`)
    if (!res.ok) return []
    const items = await res.json()
    return Array.isArray(items) ? items : []
  } catch {
    return []
  }
}

const VISITOR_ID_KEY = 'onds_visitor_id'
const LAST_VISIT_DAY_KEY = 'onds_visit_day_v2'

export const recordVisit = async () => {
  const today = new Date().toISOString().slice(0, 10)
  if (localStorage.getItem(LAST_VISIT_DAY_KEY) === today) return

  let visitorId = localStorage.getItem(VISITOR_ID_KEY)
  if (!visitorId) {
    visitorId = crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`
    localStorage.setItem(VISITOR_ID_KEY, visitorId)
  }

  const code = sessionStorage.getItem(ACCESS.codeStorageKey) || ''
  try {
    const res = await fetch(
      `${API_BASE}/onds/visit?visitorId=${encodeURIComponent(visitorId)}&code=${encodeURIComponent(code)}`,
      { method: 'POST' }
    )
    if (!res.ok) return
    const body = await res.json().catch(() => null)
    if (body?.recorded) localStorage.setItem(LAST_VISIT_DAY_KEY, today)
  } catch { /* fire-and-forget */ }
}

export const fetchVisitStats = async (code) => {
  if (!code) return null
  const res = await fetch(`${API_BASE}/onds/visits?reveal=${encodeURIComponent(code)}`)
  return res.status === 200 ? res.json() : null
}

export const getRevealCode = () => sessionStorage.getItem(PRIVACY.codeStorageKey) || ''
export const setRevealCode = (code) => sessionStorage.setItem(PRIVACY.codeStorageKey, code)
export const clearRevealCode = () => sessionStorage.removeItem(PRIVACY.codeStorageKey)

export const getRunCode = () => sessionStorage.getItem(PRIVACY.runCodeStorageKey) || ''
export const setRunCode = (code) => sessionStorage.setItem(PRIVACY.runCodeStorageKey, code)
export const clearRunCode = () => sessionStorage.removeItem(PRIVACY.runCodeStorageKey)

export const fetchPortfolio = async (code, symbol = 'ONDS') => {
  if (!code) return null
  const res = await fetch(`${API_BASE}/onds/portfolio?reveal=${encodeURIComponent(code)}&symbol=${encodeURIComponent(symbol)}`)
  if (res.status === 200) return res.json()
  if (res.status === 429) {
    const err = new Error('throttled')
    err.retryAfter = Number(res.headers.get('Retry-After')) || 0
    throw err
  }
  return null
}

const postCommittee = async (symbol, code, { force = false, cachedOnly = false, poll = false } = {}) => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), COMMITTEE_REQUEST_TIMEOUT_MS)
  const reveal = getRevealCode()
  const run = getRunCode()
  try {
    return await fetch(
      `${API_BASE}/onds/committee?symbol=${encodeURIComponent(symbol)}&code=${encodeURIComponent(code)}&force=${force ? 'true' : 'false'}&cachedOnly=${cachedOnly ? 'true' : 'false'}&poll=${poll ? 'true' : 'false'}&reveal=${encodeURIComponent(reveal)}&run=${encodeURIComponent(run)}`,
      { method: 'POST', signal: controller.signal }
    )
  } finally {
    clearTimeout(timer)
  }
}

const fetchCommittee = async (symbol, { force = false, cachedOnly = false, onStatus } = {}) => {
  const code = sessionStorage.getItem(ACCESS.codeStorageKey)
  if (!code) return null

  if (cachedOnly) {
    const res = await postCommittee(symbol, code, { cachedOnly: true })
    return res.status === 200 ? res.json() : null
  }

  const deadline = Date.now() + COMMITTEE_MAX_WAIT_MS
  let triggered = false

  while (Date.now() < deadline) {
    const res = triggered
      ? await postCommittee(symbol, code, { poll: true })
      : await postCommittee(symbol, code, { force })
    triggered = true

    if (res.status === 200) return res.json()
    if (res.status === 202) {
      let progress = null
      try { progress = (await res.json())?.progress } catch { /* status body optional */ }
      onStatus?.(progress || {})
      await sleep(COMMITTEE_POLL_INTERVAL_MS)
      continue
    }
    if (res.status === 401) {
      clearRunCode()
      const err = new Error('run auth required')
      err.auth = 'run'
      throw err
    }
    if (res.status === 429) {
      const err = new Error('throttled')
      err.retryAfter = Number(res.headers.get('Retry-After')) || 0
      throw err
    }
    if (res.status === 409) throw new Error('committee run did not complete')
    throw new Error(`committee request failed: ${res.status}`)
  }

  throw new Error('committee timed out')
}

const firstSentence = (text) => {
  if (!text) return ''
  const match = text.match(/[^.!?]*[.!?]/)
  return (match ? match[0] : text).trim()
}

const STANCE_BY_ACTION = {
  BUY_MORE: 'bull',
  HOLD: 'caution',
  TRIM: 'caution',
  SELL_ALL: 'bear',
  ROTATE: 'bear',
}

const mapVerdict = (v) => ({
  action: v.action,
  consensusRatio: (v.consensusRatio ?? 0) / 100,
  sizing: [v.sizing, v.marginGuidance ? `Margin: ${v.marginGuidance}` : null].filter(Boolean).join('\n\n'),
  headline: firstSentence(v.rationale) || v.sizing || 'Committee verdict',
  rationale: v.rationale,
  priceTarget: v.priceTarget ?? null,
  targetTimeframe: v.targetTimeframe,
  goalNote: v.goalNote,
  reversalTrigger: v.reversalTrigger,
  exitHunt: v.exitHunt,
  rotateTarget: v.rotateTarget,
  dissent: (v.dissents ?? []).map((note) => ({ memberId: 'Committee', note })),
})

const mapCommittee = (opinions) =>
  opinions.map((o) => ({
    memberId: o.key,
    name: o.name,
    style: o.style,
    action: o.action,
    conviction: (o.conviction ?? 0) / 100,
    targetPrice: o.targetPrice ?? null,
    horizon: 'current view',
    thesis: o.thesis,
    risks: o.risks ?? [],
  }))

const mapDebate = (opinions) =>
  opinions.map((o) => ({
    memberId: o.key,
    name: o.name,
    style: o.style,
    stance: STANCE_BY_ACTION[o.action] ?? 'caution',
    quote: firstSentence(o.thesis) || o.thesis || '',
  }))

const mapBrief = (markdown, symbol) => {
  if (!markdown || !markdown.trim()) return null

  const lines = markdown.replace(/\r/g, '').split('\n')
  const blocks = []
  let current = []
  let title = null

  const flush = () => {
    if (current.length) {
      blocks.push(current.join(' ').trim())
      current = []
    }
  }

  for (const raw of lines) {
    const line = raw.trim()
    if (!line) {
      flush()
      continue
    }
    const heading = line.match(/^#{1,6}\s+(.*)$/)
    if (heading) {
      flush()
      if (!title) title = heading[1].trim()
      else blocks.push(heading[1].trim())
      continue
    }
    current.push(line.replace(/^[-*]\s+/, '').replace(/[*_`>]/g, ''))
  }
  flush()

  const paragraphs = blocks.filter(Boolean)
  const lead = paragraphs.shift() ?? ''
  const words = markdown.split(/\s+/).length

  return {
    edition: 'Committee Edition',
    title: title || 'Committee Brief',
    author: `The ${symbol || 'ONDS'} Investment Committee`,
    dateline: 'Live',
    readMinutes: Math.max(1, Math.round(words / 200)),
    lead,
    paragraphs,
    signoff: 'To the number.',
  }
}

export const mergeCommittee = (data, committee) => {
  if (!committee || !committee.verdict) return data

  const next = { ...data }
  applyCommittee(next, committee)
  return next
}

const withId = (arr, prefix) => (arr ?? []).map((x, i) => ({ id: x.id || `${prefix}${i}`, ...x }))

const applyExtras = (data, extras) => {
  if (!extras) return

  if (Array.isArray(extras.scenarios) && extras.scenarios.length)
    data.scenarios = withId(extras.scenarios, 'sc')

  if (Array.isArray(extras.catalysts) && extras.catalysts.length)
    data.catalysts = extras.catalysts.map((c, i) => ({
      id: `cat${i}`,
      title: c.title,
      type: c.type,
      at: new Date(Date.now() + (c.inDays ?? 0) * 86400000).toISOString(),
      expectedImpact: c.expectedImpact,
      note: c.note,
    }))

  if (Array.isArray(extras.tradeIdeas) && extras.tradeIdeas.length)
    data.tradeIdeas = withId(extras.tradeIdeas, 'ti')

  if (Array.isArray(extras.alternatives) && extras.alternatives.length)
    data.alternatives = extras.alternatives

  if (extras.leverage) {
    data.leverage = {
      currentDebt: extras.leverage.currentDebt,
      recommendedDebt: extras.leverage.recommendedDebt,
      maxDebt: extras.leverage.maxDebt,
      triggerCondition: extras.leverage.triggerCondition,
      rationale: extras.leverage.rationale,
    }
  }

  if (Array.isArray(extras.holdings) && extras.holdings.length)
    data.holdings = withId(extras.holdings, 'hold')

  if (extras.moat && (extras.moat.summary || (extras.moat.factors ?? []).length))
    data.moat = extras.moat

  if (extras.shortInterest && (extras.shortInterest.summary || extras.shortInterest.thesis))
    data.shortInterest = extras.shortInterest

  if (extras.insiders && (extras.insiders.summary || (extras.insiders.transactions ?? []).length))
    data.insiders = extras.insiders

  if (extras.bearCase && (extras.bearCase.summary || (extras.bearCase.claims ?? []).length))
    data.bearCase = extras.bearCase

  if (extras.houseView) data.houseView = extras.houseView

  if (extras.news) {
    const norm = (arr) => (arr ?? []).map((n, i) => ({
      id: `n${i}`,
      source: n.source,
      title: n.title,
      summary: n.summary,
      impact: n.impact,
      implication: n.implication,
      official: !!n.official,
      publishedAt: n.publishedAt || data.asOf,
    }))
    const macro = norm(extras.news.macro)
    const sector = norm(extras.news.sector)
    const onds = norm(extras.news.onds)
    if (macro.length || sector.length || onds.length) data.news = { macro, sector, onds }
  }
}

const applyCommittee = (data, committee) => {
  if (!committee || !committee.verdict) return

  data.verdict = mapVerdict(committee.verdict)

  if (Array.isArray(committee.opinions) && committee.opinions.length > 0) {
    data.committee = mapCommittee(committee.opinions)
    data.debate = mapDebate(committee.opinions)
  }

  const brief = mapBrief(committee.brief, committee.symbol || data.quote?.ticker)
  if (brief) data.brief = brief

  applyExtras(data, committee.extras)

  if (committee.quote && committee.quote.price > 0) {
    data.quote = { ...data.quote, ...committee.quote }
  }

  data.source = committee.source || 'LIVE_COMMITTEE'
  data.asOf = committee.asOf || data.asOf
}

export const hasCommitteeAccess = () => !!sessionStorage.getItem(ACCESS.codeStorageKey)

export { fetchCommittee }

const emptyDesk = (symbol) => ({
  source: 'LIVE_QUOTE',
  asOf: new Date().toISOString(),
  quote: { ticker: symbol, price: 0, prevClose: 0, dayChange: 0, dayChangeRatio: 0, volume: 0, avgVolume: 0 },
  headlines: [],
  brief: null,
  news: { macro: [], sector: [], onds: [] },
  houseView: null,
  holdings: [],
  tradeIdeas: [],
  leverage: null,
  scenarios: [],
  catalysts: [],
  debate: [],
  committee: [],
  verdict: null,
  alternatives: [],
})

export const fetchDashboard = async (symbol = 'ONDS') => {
  const data = symbol === 'ONDS' ? buildMockDashboard() : emptyDesk(symbol)

  try {
    const live = await fetchLiveQuote(symbol)
    if (live && live.price > 0) {
      data.quote = { ...data.quote, ...live }
      data.source = 'LIVE_QUOTE'
      data.asOf = live.asOf || new Date().toISOString()
    }
  } catch {
    // Keep the simulated quote if the live provider is unavailable
  }

  return data
}
